import classnames from 'classnames';
import React, { ReactElement, ReactNode } from 'react';

import { SvgIconNames } from '~/design-tokens/iconography/SvgIcons';

import { Badge, BadgeColor } from '../badge/Badge';
import { Icon } from '../icon/Icon';
import { Tooltip, TooltipProps, TooltipTrigger } from '../tooltip/Tooltip';
import styles from './TabLines.module.scss';

export interface Tab<T> {
  tabKey: T;
  tabLabel?: ReactNode;
  badgeLabel?: string;
  badgeColor?: BadgeColor;
  imgNode?: ReactNode;
  iconName?: SvgIconNames;
  customBottomElement?: ReactElement;
  tooltip?: Omit<TooltipProps, 'children'>;
  renderPanel?: ReactNode;
  disabled?: boolean;
}

export interface TabLinesProps<T> {
  tabs: Tab<T>[];
  activeTab: T;
  fullWidth?: boolean;
  className?: string;
  onChange: (index: number, tabKey: T) => void;
}

export const TabLines = <T extends string>({
  tabs,
  activeTab,
  onChange,
  fullWidth,
  className,
  ...rest
}: TabLinesProps<T>) => {
  const currentPanel = tabs.find(i => i.tabKey === activeTab)?.renderPanel;

  return (
    <section
      className={classnames(styles.tabContainer, className)}
      data-testid="tab-lines"
      {...rest}
    >
      <header className={styles.header} data-tab-header role="tablist">
        {tabs.map(
          (
            {
              tabKey,
              tabLabel,
              badgeLabel,
              badgeColor,
              tooltip,
              iconName,
              customBottomElement,
              disabled = false,
            },
            index
          ) => {
            const isActive = activeTab === tabKey;
            const tabId = `tablines-tab-${tabKey}`;
            const panelId = `tablines-panel-${tabKey}`;

            return (
              <Tooltip
                key={tabKey}
                disabled={disabled || !tooltip?.label}
                label={tooltip?.label}
                closeLabel={tooltip?.closeLabel ? tooltip.closeLabel : ''}
                isTouchDevice={
                  tooltip?.isTouchDevice ? tooltip.isTouchDevice : false
                }
                strategy="fixed"
                placement="top"
                trigger={[TooltipTrigger.Hover]}
                inPortal
              >
                <div
                  className={classnames(styles.tab, {
                    [styles.tabFullWidth]: fullWidth,
                  })}
                  data-testid={`tab-container-${tabKey}`}
                  data-tab
                >
                  <button
                    type="button"
                    id={tabId}
                    className={classnames(styles.tabButton, {
                      [styles.tabButtonFullWidth]: fullWidth,
                      [styles.tabButtonActive]: isActive,
                    })}
                    onClick={() => {
                      onChange(index, tabKey);
                    }}
                    data-testid={`tab-${tabKey}`}
                    disabled={disabled}
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={panelId}
                    data-tab-button
                  >
                    {iconName && (
                      <span className={styles.iconWrapper}>
                        <Icon
                          className={classnames(styles.tabIcon, {
                            [styles.tabIconActive]: isActive,
                          })}
                          iconName={iconName}
                          size={20}
                          fillColor="var(--text-on-primary)"
                        />
                      </span>
                    )}
                    {tabLabel}
                    {customBottomElement}
                  </button>
                  {badgeLabel && (
                    <div data-testid="tab-badge">
                      <Badge
                        className={styles.badge}
                        label={badgeLabel}
                        variant="numerical"
                        color={badgeColor}
                      />
                    </div>
                  )}
                </div>
              </Tooltip>
            );
          }
        )}
      </header>
      <div
        className={styles.tabPanel}
        role="tabpanel"
        id={`tablines-panel-${activeTab}`}
        aria-labelledby={`tablines-tab-${activeTab}`}
      >
        {currentPanel}
      </div>
    </section>
  );
};
